import clsx from "clsx";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { CircularProgress, makeStyles } from "@material-ui/core";

import { readCompanyRoles} from "@cargotic/webapp/resource";

import ContactEmployeeDeleteConfirmationAlert
  from "../ContactEmployeeDeleteConfirmationAlert";
import ContactEmployeeEditor from "../ContactEmployeeEditor";
import ContactSubcontractorEmployeeEditor from "../ContactSubcontractorEmployeeEditor";
import ContactEmployeeGrid from "../ContactEmployeeGrid";
import ContactEmployeeOverviewSwitchboard
  from "./ContactEmployeeOverviewSwitchboard";

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: "flex",
    flexDirection: "column",

    "& > :not(:first-child)": {
      marginTop: spacing(4)
    }
  },
  content: {
    flex: 1
  }
}));

const ContactEmployeeOverview = ({
  className,
  contact: {
    id,
    name,
    type,
    isSubcontractor
  },
  employees,
  query,
  selectedEmployee,
  isEmployeeEditorOpen,
  isSubcontractorEmployeeEditorOpen,
  isDeleteConfirmationAlertOpen,
  isLoading,
  onBreadcrumbsChange,
  onDeleteConfirmationAlertClose,
  onDeleteConfirmationAlertSubmit,
  onEditorClose,
  onEditorSubmit,
  onEmployeeAdd,
  onSubcontractorEmployeeSubmit,
  onSubcontractorEmployeeAdd,
  onSubcontractorEmployeeClose,
  onEmployeeDelete,
  onEmployeeEdit,
  onQueryChange
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [roles, setRoles] = useState()

  const handleEmployeeAddButtonClick = () => {
    if (onEmployeeAdd) {
      onEmployeeAdd();
    }
  };

  const handleAddDriverClick = () => {
    if (typeof onSubcontractorEmployeeAdd === "function") {
      onSubcontractorEmployeeAdd();
    }
  }

  const handlePageChange = value => {
    if (onQueryChange && query) {
      onQueryChange({ ...query, page: value });
    }
  };

  const handleRowsPerPageChange = value => {
    if (onQueryChange && query) {
      onQueryChange({ ...query, rowsPerPage: value });
    }
  };

  const handleSearchChange = value => {
    if (onQueryChange && query) {
      onQueryChange({ ...query, match: { ...query.match, search: value } });
    }
  };

  const handleSubmitSubcontractorEmployee = useCallback((values) => {
    if (typeof onSubcontractorEmployeeSubmit !== "undefined") {
      return onSubcontractorEmployeeSubmit(id, values)
    }
  }, [id, onSubcontractorEmployeeSubmit])

  useEffect(() => {
    const loadAvailableRoles = async () => {
      const roles = await readCompanyRoles();
      setRoles(roles);
    }
    loadAvailableRoles()
  }, [readCompanyRoles, setRoles])

  useEffect(() => {
    onBreadcrumbsChange([
      { label: t("webapp:common.title.contacts"), href: "/contacts" },
      { label: name, href: `/contacts/${id}` },
      { label: t("webapp:common.title.employees") }
    ]);
  }, []);

  useEffect(() => {
    onQueryChange({
      match: { search: "" },
      page: 0,
      rowsPerPage: 10
    });
  }, []);

  return (
    <>
      <div className={clsx(className, classes.root)}>
        <ContactEmployeeOverviewSwitchboard
          search={query?.match?.search ?? ""}
          onSearchChange={handleSearchChange}
          onAddEmployeeClick={handleEmployeeAddButtonClick}
          onAddDriverClick={handleAddDriverClick}
          contactType={type}
          isSubcontractor={isSubcontractor}
        />
        <ContactEmployeeGrid
          className={classes.content}
          employees={employees}
          page={query?.page}
          rowsPerPage={query?.rowsPerPage}
          isLoading={isLoading}
          onEmployeeDelete={onEmployeeDelete}
          onEmployeeEdit={onEmployeeEdit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
      <ContactEmployeeDeleteConfirmationAlert
        isOpen={isDeleteConfirmationAlertOpen}
        onClose={onDeleteConfirmationAlertClose}
        onSubmit={onDeleteConfirmationAlertSubmit}
      />
      <ContactEmployeeEditor
        initialValue={selectedEmployee}
        isOpen={isEmployeeEditorOpen}
        onClose={onEditorClose}
        onSubmit={onEditorSubmit}
      />
      <ContactSubcontractorEmployeeEditor
        initialValue={selectedEmployee}
        isOpen={isSubcontractorEmployeeEditorOpen}
        onClose={onSubcontractorEmployeeClose}
        onSubmit={handleSubmitSubcontractorEmployee}
        availableCompanyRoles={roles}
      />
    </>
  );
};

export default ContactEmployeeOverview;
